<template>
    <div class="telekom-angebote-glasfaser">
        <BasicBreadcrumb
            :items="[
                { label: 'Telekom Angebote', to: `/markt/${storeslug}/telekom-angebote` },
                { label: 'Glasfaser', to: '' }
            ]"
        />
        <div class="lg:container mx-auto p-8 lg:px-0 tariffSlider">
            <ContentHeroBanner
                v-for="banner in heroBanner?.items" :key="banner?.id"
                :img-mobile="banner?.imageUrlMobile"
                :img-desktop="banner?.imageUrl"
                :title="banner?.title"
                :background-img-mobile="backgroundImgMobile"
                :background-img-desktop="backgroundImgDesktop"
            />
            <div class="py-2">
                <BasicBtErrorBoundary>
                    <TariffSlider
                        :key="Date.now()"
                        :tenant-id="tenantId"
                        :sub-tenant="false"
                        :fibre="true"
                        :title="'<strong>Highspeed-Internet mit Glasfaser</strong><br/>Glasfaser-Tarife von 150 bis 1.000 MBit/s'"
                        :store="currentStore"
                    />
                </BasicBtErrorBoundary>
            </div>
            <section class="container mx-auto px-4 py-8">
                <h2 class="text-3xl font-bold text-center mb-6">
                    Ihre Vorteile mit Glasfaser der Telekom
                </h2>
                <p class="text-center text-lg mb-4">
                    Heute im Netz der Zukunft surfen.
                </p>
                <p class="text-center max-w-3xl mx-auto mb-10">
                    Tauchen Sie ein in die Welt des Highspeed-Internets der Zukunft: Schnell, zuverlässig und eine echte Bereicherung für den Wert Ihrer Immobilie.
                </p>
                <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                    <div class="p-6 rounded-2xl">
                        <h3 class="text-xl font-semibold mb-2">Effizient</h3>
                        <p>
                            Um Zeit und Kosten zu sparen, koordinieren wir unsere Aktivitäten eng mit örtlichen Unternehmen. Dadurch können wir sicherstellen, dass die Verlegung der Glasfaserleitungen möglichst zeitnah erfolgt.
                        </p>
                    </div>
                    <div class="p-6 rounded-2xl">
                        <h3 class="text-xl font-semibold mb-2">Verlässlich</h3>
                        <p>
                            Unser engagiertes Team steht Ihnen bei allen Bauvorhaben persönlich und zuverlässig zur Seite, um einen reibungslosen Ablauf sicherzustellen.
                        </p>
                    </div>
                    <div class="p-6 rounded-2xl">
                        <h3 class="text-xl font-semibold mb-2">Stabil</h3>
                        <p>
                            Selbst Umwelteinflüsse wie Feuchtigkeit, Kälte oder elektromagnetische Felder können den leistungsstarken Glasfaser-Kabeln nichts anhaben.
                        </p>
                    </div>
                    <div class="p-6 rounded-2xl">
                        <h3 class="text-xl font-semibold mb-2">Zukunftssicher</h3>
                        <p>
                            Durch die Verwendung von Glasfaser setzen Sie auf eine nachhaltige Technologie. Dank ihrer nahezu unbegrenzten Übertragungskapazitäten sind Sie bereits heute bestens für die technologischen Anforderungen von morgen gerüstet.
                        </p>
                    </div>
                </div>
            </section>
            <div class="py-8 glasfaserText">
                <h1 class="text-xl font-bold mb-6">Häufig gestellte Fragen zu Glasfaser</h1>
                <div v-for="(faq, index) in faqs" :key="index" class="border-b">
                    <button 
                        class="w-full text-left py-4 flex justify-between items-center focus:outline-none" 
                        @click="toggle(index)"
                    >
                        <span class="font-semibold">{{ faq.question }}</span>
                        <span class="text-xl">
                        {{ activeIndex === index ? "-" : "+" }}
                        </span>
                    </button>
                    <div 
                        v-show="activeIndex === index" 
                        class="pb-4 text-gray-700 transition-all duration-300"
                        v-html="faq.answer"
                    />
                </div>
            </div>
            <div class="pt-4 pb-12 container mx-auto font-bold text-paragraph text-2xl text-center">
                Alle Informationen und Angebote zu den Themen Mobilfunk,<br>
                MagentaTV, Internet und Kombipakete findest du hier
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 pb-8">
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconContract"
                    url="/telekom-angebote/mobilfunk"
                    :title="'Handy mit Vertrag'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconMagenta"
                    url="/telekom-angebote/magentatv"
                    :title="'MagentaTV'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconInternet"
                    url="/telekom-angebote/internet"
                    :title="'Internet'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconCombi"
                    url="/telekom-angebote/kombipakete"
                    :title="'Kombi-Pakete'"
                />
            </div>
        </div>
        <TariffEyeCatcher />
    </div>
</template>

<script setup lang="ts">

    import type { ContentBlock } from '~~/models/Tariff/ContentBlock';
    import { ContentBlockType } from '~~/models/Tariff/ContentBlock';

    import backgroundImgMobile from '~/assets/img/tariff-topic/telekom/ssb_BG_mobile.png';
    import backgroundImgDesktop from '~/assets/img/tariff-topic/telekom/ssb_BG.png';
    import backgroundTile from '~/assets/img/tariff-topic/telekom/bg_icons.png';
    import iconContract from '~/assets/img/tariff-topic/telekom/two/mobilfunk.png';
    import iconMagenta from '~/assets/img/tariff-topic/telekom/two/magentaTV.png';
    import iconInternet from '~/assets/img/tariff-topic/telekom/two/internet.png';
    import iconCombi from '~/assets/img/tariff-topic/telekom/two/kombi-pakete.png';

    const tenantId: string = "692ef56ad1b19b630ac62ff3";
    const currentStore = useCurrentStore();

    const route = useRoute();
    const storeslug = route.params.storeslug;

    const storeName = currentStore?.value?.name ?? 'expert';
    const pageTitle = 'Telekom Glasfaser bei ' + storeName + ' | Highspeed Internet bis 1.000 Mbit/s bei expert';
    const pageDescription = 'Telekom Glasfaser bei ' + storeName + ': Highspeed Internet mit 150 bis 1.000 Mbit/s, attraktiven Angeboten & Aktionen für Neukunden. Jetzt informieren!';
    useHead({
        title: pageTitle,
        meta: [{ name: 'description', content: pageDescription }
        ]
    });
    
    const { data: heroBanner } = await useFetch<{ items: ContentBlock[] }>(
        `/api/nea/tariff-service/contentBlock`,
        {
            query: {
                page: 0,
                validOnly: true,
                pageSize: 1,
                type: ContentBlockType.TELEKOM_OPTICAL_FIBRE_BANNER
            }
        }
    );  

    const activeIndex = ref<number | null>(null);
    function toggle(index: number) {
        activeIndex.value = activeIndex.value === index ? null : index;
    }    
    const faqs = [
        {
            question: "Was ist Glasfaser?",
            answer: `
            Eine Glasfaser ist ein extrem dünner Glasfaden, der in der Telekommunikation als Lichtwellenleiter vorzugsweise für Highspeed-Internetverbindungen verwendet wird. Daten werden durch Glasfasern nahezu mit Lichtgeschwindigkeit übertragen. 
            Im Gegensatz zu Kupferkabeln erfolgt diese Übertragung nicht elektrisch, sondern optisch. Dadurch ermöglichen Glasfaseranschlüsse eine wesentlich höhere 
            Bandbreite für die Datenübertragung. Der innere Kern der Glasfaser, der aus reinem Glas besteht, transportiert Informationen in Form von Lichtimpulsen. 
            Dieser Kern wird von einem Mantel umgeben, der verhindert, dass Licht austritt. Eine äußere Kunststoffhülle schützt die empfindliche Leitung zusätzlich.`
        },
        {
            question: "Welche Vorteile bietet Glasfaser?",
            answer: `
            Ein erheblicher Vorteil von Glasfaser liegt in seiner Highspeed-Technologie, die die Übertragung großer Datenmengen für sowohl den Download als auch den 
            Upload ermöglicht. Glasfaser ist äußerst leistungsstark und ermöglicht das Surfen mit sehr hohen Geschwindigkeiten. Zudem zeichnet sich Glasfaser durch 
            eine stabile Leistung aus und ist selbst bei einer hohen Anzahl von Nutzern weniger anfällig für Störungen. Ein weiterer Vorteil ist die ökologische 
            Verträglichkeit: Glasfaser verbraucht weniger Energie als Kupfernetze und erzeugt keine elektromagnetische Strahlung.`
        },
        {
            question: "Was ist der Unterschied zwischen Glasfaser, DSL und Kabel?",
            answer: `
            Der wesentliche Unterschied besteht darin, dass Glasfaser eine deutlich höhere Übertragungskapazität bietet und weniger Signalverluste aufweist. 
            Im Vergleich zu DSL und Kabel ist Glasfaser leistungsstärker und besser für große Datenmengen geeignet. Zudem sind Glasfaserkabel weniger störanfällig 
            und bieten eine konstante Bandbreite.`
        },
        {
            question: "Für wen lohnt sich Glasfaser?",
            answer: `
            Glasfaser lohnt sich für alle, die eine stabile Internetverbindung und hohe Bandbreiten benötigen – z. B. für Streaming, Homeoffice oder Gaming. 
            Auch bei mehreren gleichzeitigen Nutzern bleibt die Verbindung stabil. Wichtig ist jedoch, vorab zu prüfen, ob Glasfaser an Ihrem Wohnort verfügbar ist.`
        },
        {
            question: "Kann ich meinen Glasfaser-Tarif während der Laufzeit anpassen?",
            answer: `
            Die Glasfaser-Tarife haben eine Mindestvertragslaufzeit von 24 Monaten. Während dieser Zeit ist ein Wechsel in einen leistungsstärkeren Tarif jederzeit möglich.`
        },
        {
            question: "Wie kann ich meinen Glasfaser-Tarif wechseln?",
            answer: `
            Als bestehender Kunde können Sie den Tarifwechsel im Kundencenter mit Ihrem Telekom-Login durchführen. 
            Wenn Sie von einem anderen Anbieter wechseln, geben Sie dies einfach bei der Buchung an. Die Telekom kümmert sich um die Kündigung Ihres bisherigen Vertrags.`
        }
    ]

    await checkStoreHasPage(tenantId);

    watch(currentStore, async (store) => {
        const storeId: string = store?.id;
        if (!storeId) {
            return;
        }
        await checkStoreHasPage(tenantId);
    });      
</script>
<style>

    .glasfaserText p {
        margin-bottom: 25px;
    }

</style>